import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/index.css';
import '../styles/home-hero.css';
import { flagshipProducts, getProductBySlug } from '../data/products';
import { supplyChainStages } from '../data/supplyChainStages';
import SupplyChainStages from '../components/SupplyChainStages';
import Brand from '../components/Brand';
import SEO from '../components/SEO';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const goToDemo = () => navigate('/request-demo');

  return (
    <div className="home-container">
      <SEO
        title="Procurement & Inventory Software for Healthcare and SMB Operations"
        description="OpenUse builds mobile-first apps for procurement and inventory operations: SupplyMD, OpenRFQ, AInventory, and ATracker. Request a demo or try an app today."
        path="/"
      />
      {/* ===== 1. Hero ===== */}
      <div className="home-hero">
        <div className="home-hero-grid">
          <div className="home-hero-copy">
            <p className="hero-eyebrow">Digitizing the physical supply chain</p>
            <h1 className="hero-headline">Your supply chain still runs on spreadsheets.</h1>
            <p className="home-hero-subhead">
              <Brand /> digitizes the physical supply chain with four
              connected mobile apps — replacing spreadsheets with barcode
              scanning, real-time tracking, and audit trails.
            </p>
            <div className="hero-cta-row">
              <button className="hero-cta primary" onClick={goToDemo}>
                Request a demo
              </button>
              <Link to="/solutions" className="hero-cta secondary">
                Explore the platform
              </Link>
            </div>
            <div className="home-hero-apps">
              <span className="home-hero-apps-label">Four connected apps</span>
              {supplyChainStages.map((stage) => {
                const product = getProductBySlug(stage.slug);
                return (
                  <React.Fragment key={stage.slug}>
                    <span className="home-hero-apps-sep">&middot;</span>
                    <Link to={`/product/${stage.slug}`} className="home-hero-app-link">
                      {product?.name ?? stage.slug}
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="stat-panel">
            <div className="stat-panel-glow" />
            <p className="stat-panel-eyebrow">The industry today</p>
            <div className="stat-panel-number">80%</div>
            <p className="stat-panel-copy">
              of companies still run inventory and supply chain operations on
              Excel spreadsheets or paper.
            </p>
            <div className="stat-panel-divider" />
            <div className="stat-panel-transform">
              <div className="stat-panel-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                  <line x1="15" y1="3" x2="15" y2="21" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                </svg>
              </div>
              <svg className="stat-panel-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
              <div className="stat-panel-icon stat-panel-icon--accent">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="12" height="20" rx="2" />
                  <line x1="9" y1="8" x2="9" y2="16" />
                  <line x1="12" y1="6" x2="12" y2="16" />
                  <line x1="15" y1="9" x2="15" y2="16" />
                </svg>
              </div>
              <span className="stat-panel-transform-text">That's what OpenUse replaces.</span>
            </div>
            <p className="stat-panel-source">
              Source:{' '}
              <a
                className="stat-panel-source-link"
                href="https://capsresearch.org/blog/posts/2025-caps-blog-post/july/caps-stats-scm-outsourced-activities/"
                target="_blank"
                rel="noopener noreferrer"
              >
                CAPS Research, 2025
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ===== 2. Supply chain stages ===== */}
      <SupplyChainStages
        eyebrow="How it works"
        title="One connected supply chain"
        subtitle="Four purpose-built apps that hand off to each other — from requesting a quote to dispensing the item at the point of use."
      />

      {/* ===== 3. Why OpenUse ===== */}
      <section className="why-section">
        <h2 className="section-title">Built for modern operational teams</h2>
        <p className="section-lead">
          <Brand /> focuses on practical, mobile-first software designed to
          simplify operational workflows and improve decision-making across
          procurement and inventory systems.
        </p>
      </section>

      {/* ===== 4. Industries ===== */}
      <section className="industry-strip">
        <h2 className="section-title">Industries we serve</h2>
        <ul className="industry-list">
          <li>Healthcare</li>
          <li>Dental Practices</li>
          <li>Clinics</li>
          <li>SMB Operations</li>
          <li>Distribution Teams</li>
        </ul>
      </section>

      {/* ===== 5. Product Screenshots ===== */}
      <section className="screenshots-section">
        <h2 className="section-title">See the products in action</h2>
        <p className="section-subtitle">
          Clean, mobile-first interfaces for the workflows your team runs every day.
        </p>
        <div className="screenshots-grid">
          {flagshipProducts.map((p) => {
            const screenshotByName: Record<string, string> = {
              SupplyMD: '/images/products/supplymd/supplymd-card.png',
              OpenRFQ: '/images/products/openrfq/openrfq-card.png',
              AInventory: '/images/products/ainventory/ainventory-card.png',
              ATracker: '/images/products/atracker/atracker-list.png',
            };
            const screenshot = screenshotByName[p.name] ?? p.heroImage;
            return (
              <figure key={p.slug} className="device-mockup">
                <div className="device-mockup-frame">
                  <img src={screenshot} alt={`${p.name} dashboard preview`} />
                </div>
                <figcaption>
                  <strong>{p.name}</strong>
                  <span>{p.tagline}</span>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </section>

      {/* ===== 6. Founder / Mission ===== */}
      <section className="founder-section">
        <h2 className="section-title">
          Why we started <Brand />
        </h2>
        <p className="section-lead">
          We saw operational teams struggling with fragmented procurement and
          inventory workflows. <Brand /> was created to modernize these
          processes with intuitive, mobile-first software.
        </p>
        <Link to="/about" className="product-card-link">
          Read more about our mission &rsaquo;
        </Link>
      </section>

      {/* ===== 7. Final CTA ===== */}
      <section className="final-cta-section">
        <h2>Modernize your procurement and inventory operations</h2>
        <button className="hero-cta primary" onClick={goToDemo}>Request Demo</button>
      </section>
    </div>
  );
};

export default HomePage;
